import { useEffect, useState } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import {
  useQueryState,
  IconLoader,
  SchemaFilterType,
  ButtonPrimary,
  IconExport,
  ButtonSize,
  SortDir,
  usePrevious,
  PermissionEnum,
  useFilters,
  SelectItemProps,
  Table,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  setAccountId as setAccountIdAction,
  clearCardsTransactionState,
} from '../store/slices/statements';
import {
  overviewAccountsColumns,
  overviewAccountsColumnsLabels,
  overviewAccountsFiltersSchema,
  overviewAccountsSchema,
} from './variables/statements-accounts';
import StatementPanel from './parts/statements-panel';
import useStatements from './hooks/use-statements';
import { AccountTransactionsTypes, StatementsContextType } from './types';
import { OrganizationType } from '../types';

import cn from './styles.module.css';

const StatementTransactions = ({
  customerType,
  accountsPage,
}: AccountTransactionsTypes) => {
  const [statementPanelIsVisible, setStatementPanelIsVisible] = useState(false);

  const { accounts, currentAccount, balances } =
    useOutletContext<StatementsContextType>();

  const prevAccount = usePrevious(currentAccount);

  const dispatch = useAppDispatch();

  const { customerId } = useParams();

  const {
    accountsTransactionsStatements,
    accountsTransactionsStatementsErrorsList,
  } = useAppSelector((state) => state.customerStatements);
  const { baseUserInformation } = useAppSelector(
    (state) => state.individualCustomers.info.overview,
  );
  const { activePermissions, currenciesList: currencies } = useAppSelector(
    (state) => state.root,
  );

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    overviewAccountsSchema,
  );

  const { filtersComponent, setValues, handleSubmit, reset } = useFilters({
    filtersSchema: overviewAccountsFiltersSchema,
    filters,
    onFilterChange,
  });

  const {
    setAccountId,
    isLoading,
    isFetching,
    onSort,
    onFilter,
    formattedData,
    accountId,
    errorList,
    statementError,
  } = useStatements({
    statements: accountsTransactionsStatements,
    filters,
    onSortChange,
    onFilterChange,
    sort,
    customerId,
    customerType,
    accountsPage,
    accountNumber: currentAccount?.fullNumber,
  });

  useEffect(() => {
    setValues('currencyId', currencies as SelectItemProps[]);
  }, [currencies]);

  useEffect(() => {
    if (filters) {
      const nextFilters = { ...filters };

      if (nextFilters.type) {
        delete nextFilters.type;
      }

      reset(nextFilters);
    }
  }, [filters]);

  useEffect(() => {
    if (accounts && accounts.length && !currentAccount) {
      setAccountId(accounts[0].id);
      dispatch(setAccountIdAction(accounts[0].id));

      onFilter({
        ...filters,
        account: {
          value: [accounts[0].fullNumber],
          type: SchemaFilterType.STRING,
        },
        sortProp: {
          value: ['transferDate'],
          type: SchemaFilterType.STRING,
        },
        sortDir: {
          value: [SortDir.DESC],
          type: SchemaFilterType.STRING,
        },
      });
    } else if (currentAccount && currentAccount !== prevAccount) {
      setAccountId(currentAccount.id);
      dispatch(setAccountIdAction(currentAccount.id));

      onFilter({
        ...filters,
        account: {
          value: [currentAccount.fullNumber],
          type: SchemaFilterType.STRING,
        },
        sortProp: {
          value: ['transferDate'],
          type: SchemaFilterType.STRING,
        },
        sortDir: {
          value: [SortDir.DESC],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [accounts, filters, dispatch, currentAccount, prevAccount]);

  useEffect(() => {
    dispatch(clearCardsTransactionState());
  }, [dispatch]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.overviewStatementsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div>
        {currencies && (
          <Table
            columns={overviewAccountsColumns}
            data={formattedData || []}
            onSort={(name) => onSort(name)}
            filters={filters}
            sort={sort}
            columnsLabels={overviewAccountsColumnsLabels}
            error={accountsTransactionsStatementsErrorsList || errorList}
            onFilterChange={(values) => {
              if (!values.accountId) {
                onFilter({
                  accountId: {
                    value: [accountId],
                    type: SchemaFilterType.STRING,
                  },
                });
              } else {
                onFilter(values);
              }
            }}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isFetching}
            tableFormClassName={cn.overviewStatementsTableForm}
            tableHeadCellClassName={cn.overviewStatementsTableHeadCell}
            tableBodyClassName={!accountId ? cn.emptySeletedAccount : ''}
            clickable
          />
        )}
      </div>
    );
  }

  return (
    <>
      {(customerType !== OrganizationType.INDIVIDUAL ||
        (customerType === OrganizationType.INDIVIDUAL &&
          baseUserInformation?.type !== 'COMPANY_OFFICIAL')) && (
        <>
          {accounts && (
            <div className={cn.overviewStatementsAccountsWrapper}>
              {accounts.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${cn.overviewStatementsAccountButton} ${
                    accountId === item.id
                      ? cn.overviewStatementsAccountButtonActive
                      : ''
                  }`}
                  onClick={() => {
                    setAccountId(item.id);
                    dispatch(setAccountIdAction(item.id));

                    onFilter({
                      ...filters,
                      account: {
                        value: [item.fullNumber],
                        type: SchemaFilterType.STRING,
                      },
                      sortProp: {
                        value: ['transferDate'],
                        type: SchemaFilterType.STRING,
                      },
                      sortDir: {
                        value: [SortDir.DESC],
                        type: SchemaFilterType.STRING,
                      },
                    });
                  }}
                >
                  {item.fullNumber}
                </button>
              ))}
            </div>
          )}

          <div className={cn.accountsStatementsControls}>
            {activePermissions?.includes(
              PermissionEnum.ACCOUNTS_STATEMENTS_EXPORT_ACTION,
            ) && (
              <ButtonPrimary
                icon={<IconExport />}
                size={ButtonSize.SMALL}
                text="Export"
                onClick={() => setStatementPanelIsVisible(true)}
              />
            )}
            {statementError && (
              <div className={cn.downloadStatementError}>{statementError}</div>
            )}
          </div>
          <div
            className={`${cn.overviewStatementsContentTable} ${
              isFetching ? cn.tableLoading : ''
            }`}
          >
            {content}
          </div>
          <StatementPanel
            statementPanelVisible={statementPanelIsVisible}
            onCloseStatementPanel={() => setStatementPanelIsVisible(false)}
            selectedAccount={currentAccount?.id}
            accounts={accounts}
            currencies={balances
              ?.filter((item) => item.accountId === currentAccount?.id)
              ?.map((item) => item.currencyId)}
          />
        </>
      )}

      <Outlet />
    </>
  );
};

export default StatementTransactions;
